// Finding the product a patient holds, so its questionnaire can be read.
//
// A held record (an order, a subscription, a transaction) stores a product id
// and nothing else. The questionnaire lives on the product, so getting from one
// to the other is a lookup in whatever `GET /api/get-all-products` returned.
//
// That response does not list one row per product. Doses of the same medication
// are folded together: only the lowest gets a row, and the rest come back
// nested on its `variants` array. On production today that leaves 20 sellable
// ids with no row of their own, including every Sildenafil, Tadalafil and
// Vardenafil strength above the first.
//
// A plain `products.find(p => p.id === id)` therefore misses those, the form
// cannot read a questionnaire it has no product for, and the patient gets
// "Failed to load the form. Please try again."
//
// Lookups by id come through here. Lists a customer browses keep reading the
// top-level rows, so the shop still shows one card per medication with its
// doses inside it.

const idOf = (product) => {
  const raw = product && (product.id || product._id);
  if (raw == null || raw === "") return null;
  return String(raw);
};

/**
 * The product or dose with this id, or null when the catalog does not sell it.
 *
 * Null covers the cases a caller has to handle anyway: a record that names no
 * product (a declined payment writes a billing row with no product metadata), a
 * product that has been retired, and a catalog that has not loaded yet.
 */
export const findInCatalog = (products, productId) => {
  if (productId == null || productId === "" || productId === 0) return null;
  const wanted = String(productId);
  for (const product of products || []) {
    if (idOf(product) === wanted) return product;
    // `variants` is absent on most products and is only ever one level deep,
    // so a recursive walk would only invite a cycle.
    if (!Array.isArray(product && product.variants)) continue;
    const variant = product.variants.find((entry) => idOf(entry) === wanted);
    if (variant) return variant;
  }
  return null;
};

/** Every product or dose with this id. Kept for callers that expect a list. */
export const findAllInCatalog = (products, productId) => {
  const found = findInCatalog(products, productId);
  return found ? [found] : [];
};
